import _ from "lodash";
import client from "lib/apolloClient/client";

import {
  getAssessmentToolNodeDetailsFromCache,
  writeAssessmentToolNodeDetailsInCache,
} from "modules/CommonGraphqlHelpers";
import {
  createExemplarInAssessment,
  createAnecdoteInAssessment,
  createMYPObjectiveRubricInAssessment,
  createCommentAssessmentTool,
  createScoreAssessmentTool,
  createAssessmentGradeBoundaryTool,
  upsertDpAssessmentRubricsV2InAssessment,
  createAssessmentTool as _createAssessmentTool,
  deleteAssessmentTool as _deleteAssessmentTool,
  upsertGradeScaleTool,
} from "modules/AssessmentTool/AssessmentToolModule";
import { saveRubric, deleteRubric } from "Rubric/modules/RubricModule";
import {
  saveChecklist,
  deleteChecklist,
} from "Checklist/modules/ChecklistModule";
import {
  deleteSinglePointRubric,
  saveSinglePointRubric,
} from "SinglePointRubric/modules/SinglePointRubricModule";
import { updateRubricMutation } from "Rubric/modules/RubricMutation";
import { updateChecklistMutation } from "Checklist/modules/ChecklistMutation";
import { updateSinglePointRubricMutation } from "SinglePointRubric/modules/SinglePointRubricMutation";
import {
  updateAnecdotalMutation,
  updateExemplarMutation,
} from "modules/AssessmentTool/AssessmentToolMutations";
import {
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ANALYSIS,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { updateAssessmentToolTemplateConfigMutation } from "./Mutations";
import { updateAssessmentToolMutation } from "modules/AssessmentTool/AssessmentToolMutations";
import { dispatchError } from "Utils";

export const writeAssessmentToolInLocal = ({ id, params, type }) => {
  return (dispatch, getState) => {
    let data = getAssessmentToolNodeDetailsFromCache({ id, type });
    data = { ...data, ...params };
    writeAssessmentToolNodeDetailsInCache({ id, data, type });
  };
};

export const updateAssessmentTool = ({ id, params, type }) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();

    try {
      let mutation = "";
      let variables = {
        ...params,
        id,
        updatedBy: userInfo.id,
      };

      switch (type) {
        case "RUBRIC":
          mutation = updateRubricMutation;
          break;
        case "CHECKLIST":
          mutation = updateChecklistMutation;
          break;
        case "SINGLE_POINT_RUBRIC":
          mutation = updateSinglePointRubricMutation;
          break;
        case "ANECDOTE":
          mutation = updateAnecdotalMutation;
          break;
        case "EXEMPLAR":
          mutation = updateExemplarMutation;
          break;
        case SCORE_BASED_RUBRIC:
        case STANDARD_BASED_RUBRIC: {
          mutation = updateAssessmentToolMutation;
          variables = {
            input: { id, label: params?.title },
          };
          break;
        }
      }

      await client.mutate({
        mutation,
        variables,
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const deleteAssessmentTool = ({
  id,
  type,
  filters,
  showDiscardToast = false,
}) => {
  return async (dispatch, getState) => {
    switch (type) {
      case "RUBRIC":
        dispatch(
          deleteRubric({
            id,
            filters,
            showDiscardToast,
          })
        );
        break;
      case "CHECKLIST":
        dispatch(
          deleteChecklist({
            id,
            filters,
            showDiscardToast,
          })
        );
        break;
      case "SINGLE_POINT_RUBRIC":
        dispatch(
          deleteSinglePointRubric({
            id,
            filters,
            showDiscardToast,
          })
        );
        break;
      case SCORE_BASED_RUBRIC:
      case STANDARD_BASED_RUBRIC: {
        dispatch(
          _deleteAssessmentTool({
            id,
            assessmentToolType: type,
            filters,
            showDiscardToast,
          })
        );
        break;
      }
    }
  };
};

export const createAssessmentTool = ({
  type,
  isGlobal = true,
  title = "",
  copyToolId = null,
  mode = "CREATE",
  parentId,
  parentType,
  assessmentToolData,
  filters,
  curriculumProgramId,
  extraParams = {},
  assessmentId,
  portalType,
  shouldRefetchQuery = false,
}) => {
  return async (dispatch, getState) => {
    let id = null;
    switch (type) {
      case "RUBRIC":
        id = await dispatch(
          saveRubric({
            mode,
            title,
            copyToolId,
            isGlobal,
            parentId,
            parentType,
            filters,
            curriculumProgramId,
            extraParams,
            portalType,
            shouldRefetchQuery,
          })
        );
        break;
      case "CHECKLIST":
        id = await dispatch(
          saveChecklist({
            mode,
            title,
            copyToolId,
            isGlobal,
            parentId,
            parentType,
            filters,
            curriculumProgramId,
            extraParams,
            portalType,
            shouldRefetchQuery,
          })
        );
        break;
      case "SINGLE_POINT_RUBRIC":
        id = await dispatch(
          saveSinglePointRubric({
            mode,
            title,
            copyToolId,
            isGlobal,
            parentId,
            parentType,
            filters,
            curriculumProgramId,
            extraParams,
            portalType,
            shouldRefetchQuery,
          })
        );
        break;
      case "EXEMPLAR":
        id = await dispatch(
          createExemplarInAssessment({ parentId, parentType })
        );
        break;
      case "ANECDOTE":
        id = await dispatch(
          createAnecdoteInAssessment({ parentId, parentType })
        );
        break;
      case "MYP_OBJECTIVE_RUBRIC":
      case "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC":
        id = await dispatch(
          createMYPObjectiveRubricInAssessment({
            parentId,
            parentType,
            toolType: type,
            ...assessmentToolData,
            portalType,
          })
        );
        break;
      case "DP_ASSESSMENT_RUBRIC":
        id = await dispatch(
          upsertDpAssessmentRubricsV2InAssessment({
            input: assessmentToolData,
            extraParams,
            portalType,
          })
        );
        break;
      case "COMMENT":
        id = await dispatch(
          createCommentAssessmentTool({
            parentId,
            parentType,
            assessmentId,
            portalType,
          })
        );
        break;

      case "SCORE":
        id = await dispatch(
          createScoreAssessmentTool({
            ...extraParams,
            parentId,
            parentType,
            assessmentId,
            portalType,
          })
        );
        break;

      case ASSESSMENT_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY: {
        id = await dispatch(
          createAssessmentGradeBoundaryTool({
            ...extraParams,
            parentId,
            parentType,
            type,
            portalType,
          })
        );
        break;
      }
      case ANALYSIS:
      case SCORE_BASED_RUBRIC:
      case STANDARD_BASED_RUBRIC: {
        id = await dispatch(
          _createAssessmentTool({
            parentId,
            parentType,
            assessmentToolType: type,
            label: title,
            copyAssessmentToolId: copyToolId,
            isGlobal,
            shouldRefetchQuery,
            filters,
            portalType,
            extraParams,
          })
        );

        break;
      }
      case GRADE_SCALE_TOOL: {
        id = await dispatch(
          upsertGradeScaleTool({
            parentId,
            parentType,
            extraParams,
          })
        );
      }
    }
    return id;
  };
};

export const updateAssessmentToolTemplateConfig = ({ input, type }) => {
  return async dispatch => {
    try {
      const result = await client.mutate({
        mutation: updateAssessmentToolTemplateConfigMutation,
        variables: { input },
      });

      const isSuccess = _.get(
        result,
        "data.planner.updateAssessmentToolTemplateConfig.isSuccess",
        false
      );

      const toastType = isSuccess ? "success" : "warning";
      let toastMsg;
      if (isSuccess) {
        switch (type) {
          case "RUBRIC":
            toastMsg =
              "toastMsgs:default_levels_for_rubric_successfully_updated";
            break;
          case "SINGLE_POINT_RUBRIC":
            toastMsg =
              "toastMsgs:default_headers_for_single_point_rubric_successfully_updated";
            break;
          case SCORE_BASED_RUBRIC:
            toastMsg =
              "toastMsgs:default_levels_for_score_based_rubric_successfully_updated";
            break;
          default:
            toastMsg =
              "toastMsgs:default_options_for_checklist_successfully_updated";
        }
      } else {
        toastMsg = "toastMsgs:something_went_wrong";
      }

      dispatch(
        setToastMsg({
          type: toastType,
          msg: toastMsg,
        })
      );
    } catch (e) {
      console.error(e);
      dispatchError({ dispatch, e });
    }
  };
};
